import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    Grid,
    CardContent,
    IconButton,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { auth, db } from "../firebase";
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    getDocs,
    Timestamp,
    orderBy,
    doc,
    updateDoc,
    deleteDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";

function Tablereservation() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const [propertyId, setPropertyId] = useState("");
    const [customers, setCustomers] = useState([]);
    const [tables, setTables] = useState([]);
    const [reservations, setReservations] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 5;

    const [form, setForm] = useState({
        customerId: "",
        customerName: "",
        tableId: "",
        tableName: "",
        date: "",
        time: "",
        status: "Booked",
    });

    /* ================= PROPERTY ================= */
    useEffect(() => {
        if (!auth.currentUser) return;

        const q = query(
            collection(db, "authroles"),
            where("uid", "==", auth.currentUser.uid)
        );

        const unsub = onSnapshot(q, (snap) => {
            snap.forEach((doc) => {
                setPropertyId(doc.data().propertyId);
            });
        });

        return () => unsub();
    }, []);

    /* ================= FETCH DATA ================= */
    useEffect(() => {
        if (!propertyId) return;

        const custQ = query(
            collection(db, "restocustomers"),
            where("propertyId", "==", propertyId)
        );

        const tableQ = query(
            collection(db, "seatings"),
            where("propertyId", "==", propertyId)
        );

        const resQ = query(
            collection(db, "tablereservations"),
            where("propertyId", "==", propertyId),
            orderBy("date", "asc"),
            orderBy("time", "asc")
        );
        const unsub1 = onSnapshot(custQ, (snap) =>
            setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const unsub2 = onSnapshot(tableQ, (snap) =>
            setTables(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        const unsub3 = onSnapshot(resQ, (snap) =>
            setReservations(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        );

        return () => {
            unsub1();
            unsub2();
            unsub3();
        };
    }, [propertyId]);

    /* ================= SEARCH ================= */
    const filteredReservations = useMemo(() => {
        return reservations.filter(
            (r) =>
                r.customerName?.toLowerCase().includes(search.toLowerCase()) ||
                r.tableName?.toLowerCase().includes(search.toLowerCase())
        );
    }, [reservations, search]);

    const paginatedReservations = filteredReservations.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    /* ================= RESET FORM ================= */
    const resetForm = () => {
        setForm({
            customerId: "",
            customerName: "",
            tableId: "",
            tableName: "",
            date: "",
            time: "",
            status: "Booked",
        });
        setEditId(null);
        setIsEdit(false);
    };

    /* ================= CHECK AVAILABILITY ================= */
    const checkAvailability = async () => {
        const q = query(
            collection(db, "tablereservations"),
            where("propertyId", "==", propertyId),
            where("tableId", "==", form.tableId),
            where("date", "==", form.date),
            where("time", "==", form.time),
            where("status", "==", "Booked")
        );

        const snap = await getDocs(q);

        if (isEdit) {
            return snap.docs.every((d) => d.id === editId);
        }

        return snap.empty;
    };

    /* ================= SAVE / UPDATE ================= */
    const handleSave = async () => {
        if (!form.customerId || !form.tableId || !form.date || !form.time) {
            Toast.fire({ icon: "error", title: "Fill all fields!" });
            return;
        }

        const available = await checkAvailability();
        if (!available) {
            Toast.fire({ icon: "error", title: "Table already booked!" });
            return;
        }

        try {
            if (isEdit) {
                await updateDoc(doc(db, "tablereservations", editId), {
                    ...form,
                });
                Toast.fire({ icon: "success", title: "Updated successfully!" });
            } else {
                await addDoc(collection(db, "tablereservations"), {
                    propertyId,
                    ...form,
                    createdAt: Timestamp.now(),
                });
                Toast.fire({ icon: "success", title: "Reservation added!" });
            }

            resetForm();
            setOpenDialog(false);
        } catch (error) {
            Toast.fire({ icon: "error", title: error.message });
        }
    };

    /* ================= EDIT ================= */
    const handleEdit = (row) => {
        setForm(row);
        setEditId(row.id);
        setIsEdit(true);
        setOpenDialog(true);
    };

    /* ================= DELETE ================= */
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "This reservation will be deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });

        if (confirm.isConfirmed) {
            await deleteDoc(doc(db, "tablereservations", id));
            Toast.fire({ icon: "success", title: "Deleted successfully!" });
        }
    };

    return (
        <Box p={3}>
            <Card>
                <CardContent>
                    <Typography variant="h5" mb={2}>
                        Table Reservations
                    </Typography>

                    {/* Search + Button */}
                    <Grid container justifyContent="space-between" mb={2}>
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetForm();
                                setOpenDialog(true);
                            }}
                        >
                            Add Reservation
                        </Button>
                    </Grid>

                    {/* Table */}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr No</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Table</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedReservations.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        {(page - 1) * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell>{row.customerName}</TableCell>
                                    <TableCell>{row.tableName}</TableCell>
                                    <TableCell>{row.date}</TableCell>
                                    <TableCell>{row.time}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell align="center">
                                        <IconButton onClick={() => handleEdit(row)}>
                                            <EditIcon color="primary" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(row.id)}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    <Box mt={2} display="flex" justifyContent="center">
                        <Pagination
                            count={Math.ceil(filteredReservations.length / rowsPerPage)}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* DIALOG */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
                <DialogTitle>
                    {isEdit ? "Update Reservation" : "Add Reservation"}
                </DialogTitle>
                <DialogContent>
                    <Autocomplete
                        options={customers}
                        size="small"
                        getOptionLabel={(o) =>
                            `${o.name || ""} (${o.mobile || ""})`
                        }
                        value={customers.find((c) => c.id === form.customerId) || null}
                        onChange={(e, val) =>
                            setForm({
                                ...form,
                                customerId: val?.id || "",
                                customerName: val?.name || "",
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Customer" margin="dense" />
                        )}
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        margin="dense"
                        label="Table"
                        value={form.tableId}
                        onChange={(e) => {
                            const table = tables.find((t) => t.id === e.target.value);
                            setForm({
                                ...form,
                                tableId: table.id,
                                tableName: table.seatname,
                            });
                        }}
                    >
                        {tables.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.seatname}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        type="date"
                        fullWidth
                        size="small"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        value={form.date}
                        onChange={(e) =>
                            setForm({ ...form, date: e.target.value })
                        }
                    />

                    <TextField
                        type="time"
                        fullWidth
                        size="small"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        value={form.time}
                        onChange={(e) =>
                            setForm({ ...form, time: e.target.value })
                        }
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        margin="dense"
                        label="Status"
                        value={form.status}
                        onChange={(e) =>
                            setForm({ ...form, status: e.target.value })
                        }
                    >
                        <MenuItem value="Booked">Booked</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </TextField>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {isEdit ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tablereservation;